export type VoiceSessionIssuanceRetryDecision =
  | {
      action: "retry";
      startAttemptId: string | null;
    }
  | {
      action: "fail";
      reason: "server_shutdown_unconfirmed";
    };

export function decideVoiceSessionIssuanceRetry(input: {
  startAttemptId: string;
  hasConnectionDetails: boolean;
  serverShutdownSucceeded: boolean;
}): VoiceSessionIssuanceRetryDecision {
  if (!input.hasConnectionDetails) {
    return { action: "retry", startAttemptId: input.startAttemptId };
  }

  if (input.serverShutdownSucceeded) {
    return { action: "retry", startAttemptId: null };
  }

  return { action: "fail", reason: "server_shutdown_unconfirmed" };
}

const UNRESOLVED_ISSUANCE_CODES = new Set([
  "VOICE_SESSION_ISSUANCE_IN_PROGRESS",
  "START_ATTEMPT_EXPIRED",
]);
const DEFAULT_ISSUANCE_RETRY_AFTER_MS = 1000;
const MIN_ISSUANCE_RETRY_AFTER_MS = 250;
const MAX_ISSUANCE_RETRY_AFTER_MS = 2000;

export function parseVoiceSessionIssuanceInProgressRetry(input: {
  status: number;
  payload: unknown;
  retryAfterHeader: string | null;
}): { retryAfterMs: number } | null {
  if (input.status !== 409 || !input.payload || typeof input.payload !== "object") return null;

  const code = (input.payload as { code?: unknown }).code;
  if (typeof code !== "string" || !UNRESOLVED_ISSUANCE_CODES.has(code)) return null;

  const retryAfterSeconds = Number(input.retryAfterHeader);
  const requestedDelayMs =
    Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
      ? retryAfterSeconds * 1000
      : DEFAULT_ISSUANCE_RETRY_AFTER_MS;

  return {
    retryAfterMs: Math.min(
      MAX_ISSUANCE_RETRY_AFTER_MS,
      Math.max(MIN_ISSUANCE_RETRY_AFTER_MS, requestedDelayMs)
    ),
  };
}
